import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PiMonitorPlayBold } from 'react-icons/pi';
import { SlNotebook } from 'react-icons/sl';
import { BsListTask } from 'react-icons/bs';
import { LiaLaptopCodeSolid } from 'react-icons/lia';
import { AiOutlineFundProjectionScreen } from "react-icons/ai";
import { apiGet, apiPost } from "./utils/apiAuth";
import config from './config';
import CONSTANTS, { ContentTypeConstants } from './constants/contentCreationConstants';
import FilterTileGroup from './components/FilterTileGroup';
import { Box, Alert, Paper, Switch, FormControlLabel, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import { useToast } from './components/Toast';
import { Button, EmptyState, LoadingState } from './components/ui';

const layoutStyles = {
  container: {
    height: "100%",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  tabSection: { zIndex: 30 },
  filterSection: { top: 40, zIndex: 20, backgroundColor: "#fff" },
  subjectsContainer: { flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" },
  projectsContainer: { 
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto", 
    overflowX: "auto", 
    position: "relative", 
    zIndex: 1,
  },
  subjectContent: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" },
  tableScroll: { flex: 1, overflowY: "auto", overflowX: "auto" },
  addButton: { width: "28px", height: "28px" },
  stickyHeader: { 
    position: "sticky", 
    top: 0, 
    background: "var(--color-bg-subtle)", 
    zIndex: 1,
    boxShadow: "var(--shadow-xs)",
  },
  tableHeadCell: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: 'var(--color-text-secondary)',
    backgroundColor: 'var(--color-bg-subtle)',
    borderBottom: '1px solid var(--color-border-default)',
    whiteSpace: 'nowrap',
  },
  tableBodyCell: {
    fontSize: '0.8125rem',
    color: 'var(--color-text-primary)',
    borderBottom: '1px solid var(--color-border-default)',
    verticalAlign: 'middle',
  },
};

const tabButtonStyle = (isActive) => ({
  padding: "4px 12px",
  borderRadius: "4px",
  border: "none",
  fontSize: "13px",
  fontWeight: isActive ? 600 : 500,
  color: isActive ? "#1976d2" : "#6c757d",
  backgroundColor: isActive ? "#e3f2fd" : "transparent",
  transition: "all 0.2s ease",
});

const columnStyles = {
  sno: { width: "50px", minWidth: "50px", textAlign: "center", fontWeight: 500, padding: "4px 2px" },
  topic: { width: "180px", minWidth: "180px", fontWeight: 500, padding: "4px 4px" },
  subtopic: { width: "220px", minWidth: "220px", color: "#666", padding: "4px 4px" },
  count: { width: "70px", minWidth: "70px", textAlign: "center", fontWeight: 500, color: "#1976d2", padding: "4px 2px" },
  action: { width: "80px", minWidth: "80px", textAlign: "center", padding: "4px 2px" },
  create: { width: "100px", minWidth: "100px", textAlign: "center", padding: "4px 2px" },
};

const masterColumnStyles = {
  sno: { width: "50px", minWidth: "50px", textAlign: "center", fontWeight: 500, padding: "6px 2px" },
  subject: { width: "200px", minWidth: "200px", fontWeight: 500, padding: "6px 8px" },
  countWide: { width: "120px", minWidth: "120px", textAlign: "center", fontWeight: 500, color: "#1976d2", padding: "6px 2px" },
  count: { width: "100px", minWidth: "100px", textAlign: "center", fontWeight: 500, color: "#1976d2", padding: "6px 2px" },
};

const projectTableColumnStyles = {
  sno: { width: 32, padding: "4px 2px", textAlign: "center", fontSize: "0.75rem", fontWeight: 600 },
  projectName: { width: 128, maxWidth: 128, padding: "4px 4px", fontSize: "0.75rem", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 },
  company: { width: 88, maxWidth: 88, padding: "4px 2px", fontSize: "0.75rem", textAlign: "center", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 },
  state: { width: 72, padding: "4px 2px", fontSize: "0.75rem", textAlign: "center", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 },
  modified: { width: 92, padding: "4px 2px", fontSize: "0.7rem", textAlign: "center", lineHeight: 1.35, whiteSpace: "normal", wordBreak: "break-word" },
  domain: { width: 72, maxWidth: 72, padding: "4px 2px", fontSize: "0.75rem", textAlign: "center", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 },
  description: { width: 108, maxWidth: 108, padding: "4px 2px", fontSize: "0.75rem", textAlign: "center", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 },
  contentType: { width: 102, padding: "4px 2px", fontSize: "0.75rem", textAlign: "left", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 },
  count: { width: 46, padding: "4px 1px", fontSize: "0.75rem", textAlign: "center", fontWeight: 500, color: "#1976d2" },
};

const projectTableCell = {
  padding: "4px 2px",
  fontSize: "0.75rem",
  verticalAlign: "middle",
};

function ContentBankDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [subjectContent, setSubjectContent] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Project filter state
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectSummary, setProjectSummary] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null); // 'subject' or 'project' or null
  const projectsRestoredRef = useRef(false); // Track if projects have been restored from storage
  
  // Get user role
  const userType = sessionStorage.getItem('Type') || '';
  const isCC = userType.includes('CC') && !userType.includes('AD');

  
  // Check if we should hide count columns (when CC role and project filter is applied)
  const shouldHideCountColumns = isCC && activeFilter === 'project';
  
  // Persistence helpers and saved ids (used to reconcile after data load)
  const saveState = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ } };
  const loadState = (key, defaultValue = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : defaultValue; } catch (e) { return defaultValue; } };
  const [autoApply, setAutoApply] = useState(() => {
    try {
      const v = localStorage.getItem('ContentBankDashboard_autoApply');
      return v ? JSON.parse(v) : false;
    } catch {
      return false;
    }
  });
  const [savedSelectedSubjectIds, setSavedSelectedSubjectIds] = useState([]);
  const [savedSelectedProjectIds, setSavedSelectedProjectIds] = useState([]);

  // Navigation functions
  const navigateToContentBank = (subjectId, topicId = null, subTopicId = null) => {
    // Store filters in sessionStorage
    try {
      sessionStorage.setItem('contentBank_subjectId', JSON.stringify(subjectId));
      if (topicId) sessionStorage.setItem('contentBank_topicId', JSON.stringify(topicId));
      if (subTopicId) sessionStorage.setItem('contentBank_subTopicId', JSON.stringify(subTopicId));
    } catch (e) { /* ignore */ }
    
    navigate('/content-bank');
  };

  const handleTopicClick = (subjectId, topicId) => {
    navigateToContentBank(subjectId, topicId);
  };

  const handleSubTopicClick = (subjectId, topicId, subTopicId) => {
    navigateToContentBank(subjectId, topicId, subTopicId);
  };

  const handleViewButtonClick = (subjectId, topicId, subTopicId) => {
    navigateToContentBank(subjectId, topicId, subTopicId);
  };

  // Handle + icon click to navigate to content creation
  const handleAddContentClick = (subjectId, topicId, subTopicId) => {
    try {
      // Find the subject name from the subjects array
      const selectedSubject = subjects.find(subject => subject.subject_id === subjectId);
      const subjectName = selectedSubject ? selectedSubject.subject_name : '';
      
      // Set session storage with content creation parameters
      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(ContentTypeConstants.VIDEO.key)); // Default to Video
      sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify('level1')); // Default to level1
      sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(subTopicId));
      sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(topicId));
      sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(subjectId));
      sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
      
      // Mark subject flow so sidebar/back stay on Content Bank
      sessionStorage.setItem('contentCreation_navSource', 'content-bank');
      sessionStorage.removeItem('contentCreation_freezeFilters');
      sessionStorage.removeItem('contentCreation_projectId');
      sessionStorage.removeItem('contentCreation_projectName');
      
      // Navigate to content creation page with subject data
      navigate('/content-creation', {
        state: {
          data: {
            subject_id: subjectId,
            subject_name: subjectName,
            freezeFilters: false // Explicitly set to false to ensure filters are not frozen
          }
        }
      });
    } catch (error) {
      toast.error('Error occurred while navigating to content creation');
    }
  };

  // Clear session storage from other pages when dashboard mounts
  // This should run first, before filter restoration
  useEffect(() => {
    // Clear content creation related session storage
    try {
      sessionStorage.removeItem('contentCreation_contentType');
      sessionStorage.removeItem('contentCreation_selectedDifficulty');
      sessionStorage.removeItem('contentCreation_selectedSubTopic');
      sessionStorage.removeItem('contentCreation_selectedTopic');
      sessionStorage.removeItem('contentCreation_subjectId');
      sessionStorage.removeItem('contentCreation_subjectName');
      sessionStorage.removeItem('contentCreation_selectedVideo');
      sessionStorage.removeItem('contentCreation_selectedNote');
      sessionStorage.removeItem('contentCreation_selectedQuestion');
      sessionStorage.removeItem('contentCreation_freezeFilters');
      sessionStorage.removeItem('contentCreation_projectId');
      sessionStorage.removeItem('contentCreation_approvalStatus');
      sessionStorage.removeItem('contentCreation_batchAssigned');
      sessionStorage.removeItem('contentCreation_isAssignedContent');
      sessionStorage.removeItem('contentCreation_contentEntityType');
      sessionStorage.removeItem('contentCreation_projectIdForStatusUpdate');
      sessionStorage.removeItem('contentCreation_navSource');
      
      // Clear project content bank pending filters
      sessionStorage.removeItem('projectContentBank_pendingFilters');
      
      // Keep content bank filters (contentBank_*) so subject/topic/subtopic
      // selections survive navigation to dashboard and back.
      
      // Clear coding questions related storage
      sessionStorage.removeItem('codingQuestions_subjectId');
      sessionStorage.removeItem('codingQuestions_topicId');
      sessionStorage.removeItem('codingQuestions_subTopicId');
      sessionStorage.removeItem('codingQuestions_level');
      sessionStorage.removeItem('codingQuestions_questionId');
      sessionStorage.removeItem('codingQuestions_editor');
      sessionStorage.removeItem('codingQuestions');
      sessionStorage.removeItem('currentQuestionIndex');
      sessionStorage.removeItem('currentQuestionId');
      sessionStorage.removeItem('currentQuestionContent');
      
      // Note: We keep dashboard's own filter state (contentBankDashboard_*) for restoration
      // and topics_subtopics_* cache so content-bank filter dropdowns restore quickly.
    } catch (e) {
      // Ignore storage errors
    }
  }, []);

  // Fetch all subjects and projects on component mount
  useEffect(() => {
    fetchSubjects();
    fetchProjects();
    // Note: Filter restoration is handled in separate useEffects after data is loaded
  }, []);

  // Restore persisted UI state (tab, filters, selected ids, expanded sets)
  useEffect(() => {
    const saved = loadState('ContentBankDashboard_state');
    if (saved) {
      if (saved.activeFilter) setActiveFilter(saved.activeFilter);
      if (Array.isArray(saved.expandedSubjects)) setExpandedSubjects(new Set(saved.expandedSubjects));
      if (Array.isArray(saved.expandedProjects)) setExpandedProjects(new Set(saved.expandedProjects));
      if (Array.isArray(saved.selectedSubjectIds)) setSavedSelectedSubjectIds(saved.selectedSubjectIds);
      if (Array.isArray(saved.selectedProjectIds)) setSavedSelectedProjectIds(saved.selectedProjectIds);
    }
  }, []);

  // Initialize selected projects from sessionStorage after projects are loaded
  useEffect(() => {
    // Only restore once on initial mount when projects are loaded
    // Don't restore if user has already made changes (projectsRestoredRef prevents re-restoration)
    if (projectsRestoredRef.current) return;
    if (projects.length === 0) return;
    
    // Mark as restored immediately to prevent re-running
    projectsRestoredRef.current = true;
    
    try {
      // First try sessionStorage (most recent state)
      const projectIdsParam = sessionStorage.getItem('contentBankDashboard_selectedProjectIds');
      const activeFilterParam = sessionStorage.getItem('contentBankDashboard_activeFilter');
      
      if (projectIdsParam) {
        const projectIds = JSON.parse(projectIdsParam);
        const idSet = new Set(projectIds.map((id) => String(id)));
        const projectsToSelect = projects.filter((p) => idSet.has(String(p.id)));
        if (projectsToSelect.length > 0) {
          setSelectedProjects(projectsToSelect);
          // Check if project filter was active
          if (activeFilterParam && JSON.parse(activeFilterParam) === 'project') {
            setActiveFilter('project');
            // Apply the filter to fetch data
            handleApplyProjectFilter(projectsToSelect);
          }
          return; // Exit early if sessionStorage restore succeeded
        }
      }
    } catch (e) { /* ignore */ }
    
    // Fallback to savedSelectedProjectIds from localStorage (only on initial load)
    if (savedSelectedProjectIds.length > 0) {
      const projectsToSelect = projects.filter((p) =>
        savedSelectedProjectIds.map(String).includes(String(p.id))
      );
      if (projectsToSelect.length > 0) {
        setSelectedProjects(projectsToSelect);
        // Check localStorage saved state for activeFilter
        const savedState = loadState('ContentBankDashboard_state');
        if (savedState && savedState.activeFilter === 'project') {
          setActiveFilter('project');
          // Apply the filter to fetch data automatically
          handleApplyProjectFilter(projectsToSelect);
        }
      }
    }
  }, [projects]);

  // Reconcile persisted selected subject ids after subjects are loaded
  useEffect(() => {
    // Only restore if we don't already have selected subjects (avoid re-applying on re-renders)
    if (selectedSubjects.length > 0) return;
    // Wait for subjects to be loaded
    if (subjects.length === 0) return;
    
    // First try sessionStorage (most recent)
    try {
      const subjectIdsParam = sessionStorage.getItem('contentBankDashboard_selectedSubjectIds');
      const activeFilterParam = sessionStorage.getItem('contentBankDashboard_activeFilter');
      
      if (subjectIdsParam) {
        const subjectIds = JSON.parse(subjectIdsParam);
        const idSet = new Set(subjectIds.map((id) => String(id)));
        const subjectsToSelect = subjects.filter((s) => idSet.has(String(s.subject_id)));
        if (subjectsToSelect.length > 0) {
          setSelectedSubjects(subjectsToSelect);
          if (activeFilterParam && JSON.parse(activeFilterParam) === 'subject') {
            setActiveFilter('subject');
            fetchSubjectContent(subjectIds, subjectsToSelect);
          }
          return; // Exit early if sessionStorage restore succeeded
        }
      }
    } catch (e) { /* ignore */ }
    
    // Fallback to savedSelectedSubjectIds from localStorage
    if (savedSelectedSubjectIds.length > 0) {
      const subjectsToSelect = subjects.filter((s) =>
        savedSelectedSubjectIds.map(String).includes(String(s.subject_id))
      );
      if (subjectsToSelect.length > 0) {
        setSelectedSubjects(subjectsToSelect);
        const savedState = loadState('ContentBankDashboard_state');
        if (savedState && savedState.activeFilter === 'subject') {
          setActiveFilter('subject');
          fetchSubjectContent(
            subjectsToSelect.map((s) => s.subject_id),
            subjectsToSelect
          );
        }
      }
    }
  }, [subjects, savedSelectedSubjectIds]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`${config.API_BASE_URL}/api/get-subjects/`);
      setSubjects(response.data.subjects || []);
      setError(null);
    } catch (error) {
      setError("Failed to fetch subjects");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await apiGet(`${config.API_BASE_URL}/api/get-projects/`);
      setProjects(response.data.projects || []);
    } catch (error) {
    }
  };

  const fetchSubjectContent = async (subjectIds, subjectsList = null) => {
    try {
      setLoading(true);
      const response = await apiPost(`${config.API_BASE_URL}/api/get-subject-content/`, {
        subject_ids: subjectIds
      });
      
      const contentData = response.data.content || {};
      const idSet = new Set(subjectIds.map((id) => String(id)));
      const subjectsToUse =
        subjectsList && subjectsList.length > 0
          ? subjectsList
          : subjects.filter((s) => idSet.has(String(s.subject_id)));
      
      const completeContent = {};
      subjectsToUse.forEach(subject => {
        if (contentData[subject.subject_name]) {
          completeContent[subject.subject_name] = contentData[subject.subject_name];
        } else {
          completeContent[subject.subject_name] = [];
        }
      });
      
      setSubjectContent(completeContent);
      setError(null);
    } catch (error) {
      setError("Failed to fetch subject content");
    } finally {
      setLoading(false);
    }
  };

  // Removed fetchMasterData - master data is now in separate MasterPage component

  const applySubjectFilter = useCallback((subjectsList) => {
    if (subjectsList.length > 0) {
      setActiveFilter('subject');
      setSelectedProjects([]);
      setProjectSummary([]);
      try {
        sessionStorage.setItem('contentBankDashboard_activeFilter', JSON.stringify('subject'));
        sessionStorage.setItem('contentBankDashboard_selectedSubjectIds', JSON.stringify(subjectsList.map(s => s.subject_id)));
        sessionStorage.removeItem('contentBankDashboard_selectedProjectIds');
      } catch (e) { /* ignore */ }
      
      fetchSubjectContent(
        subjectsList.map((subject) => subject.subject_id),
        subjectsList
      );
    } else {
      setSubjectContent({});
      setActiveFilter(null);
      try {
        sessionStorage.removeItem('contentBankDashboard_activeFilter');
        sessionStorage.removeItem('contentBankDashboard_selectedSubjectIds');
      } catch (e) { /* ignore */ }
    }
  }, []);

  const applyProjectFilter = useCallback(async (projectsList) => {
    if (projectsList.length > 0) {
      setActiveFilter('project');
      setSelectedSubjects([]);
      setSubjectContent({});
      try {
        sessionStorage.setItem('contentBankDashboard_activeFilter', JSON.stringify('project'));
        sessionStorage.setItem('contentBankDashboard_selectedProjectIds', JSON.stringify(projectsList.map(p => p.id)));
        sessionStorage.removeItem('contentBankDashboard_selectedSubjectIds');
      } catch (e) { /* ignore */ }
      
      try {
        setLoading(true);
        const projectIdsArray = projectsList.map(p => p.id);
        const response = await apiPost(`${config.API_BASE_URL}/api/get-project-content-summary/`, {
          project_ids: projectIdsArray
        });
        setProjectSummary(response.data.projects || []);
        setError(null);
      } catch (err) {
        setError("Failed to fetch project summary");
      } finally {
        setLoading(false);
      }
    } else {
      setProjectSummary([]);
      setActiveFilter(null);
      try {
        sessionStorage.removeItem('contentBankDashboard_activeFilter');
        sessionStorage.removeItem('contentBankDashboard_selectedProjectIds');
      } catch (e) { /* ignore */ }
    }
  }, []);

  const handleApplyFilter = () => applySubjectFilter(selectedSubjects);

  const handleApplyProjectFilter = async (projectsToUse = null) => {
    await applyProjectFilter(projectsToUse || selectedProjects);
  };

  // Persist key pieces of UI state whenever they change
  useEffect(() => {
    saveState('ContentBankDashboard_state', {
      activeFilter,
      expandedSubjects: Array.from(expandedSubjects),
      expandedProjects: Array.from(expandedProjects),
      selectedSubjectIds: selectedSubjects.map(s => s.subject_id),
      selectedProjectIds: selectedProjects.map(p => p.id)
    });
  }, [activeFilter, expandedSubjects, expandedProjects, selectedSubjects, selectedProjects]);

  useEffect(() => {
    saveState('ContentBankDashboard_autoApply', autoApply);
  }, [autoApply]);

  const toggleSubjectExpansion = (subjectName) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectName)) {
      newExpanded.delete(subjectName);
    } else {
      newExpanded.add(subjectName);
    }
    setExpandedSubjects(newExpanded);
  };

  const toggleProjectExpansion = (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };


  const calculateTotalCounts = (subjectData) => {
    let totalVideos = 0;
    let totalNotes = 0;
    let totalMcq = 0;
    let totalCoding = 0;
    let totalProjectCoding = 0;

    subjectData.forEach(topic => {
      topic.sub_topics.forEach(subTopic => {
        totalVideos += subTopic.video ?? subTopic[ContentTypeConstants.VIDEO.key] ?? 0;
        totalNotes += subTopic.notes ?? subTopic[ContentTypeConstants.NOTES.key] ?? 0;
        totalMcq += subTopic.mcq ?? subTopic[ContentTypeConstants.MCQ.key] ?? 0;
        totalCoding += subTopic.coding ?? subTopic[ContentTypeConstants.CODING.key] ?? 0;
        totalProjectCoding +=
          subTopic.project_coding ??
          subTopic[ContentTypeConstants.PROJECT_CODING.key] ??
          0;
      });
    });

    return { totalVideos, totalNotes, totalMcq, totalCoding, totalProjectCoding };
  };

  const handleSubjectSelect = (subject) => {
    const isSelected = selectedSubjects.some(s => s.subject_id === subject.subject_id);
    let nextSelected;

    if (isSelected) {
      nextSelected = selectedSubjects.filter(s => s.subject_id !== subject.subject_id);
    } else {
      if (selectedSubjects.length >= 4) {
        toast.warning("Maximum 4 subjects can be selected");
        return;
      }
      nextSelected = [...selectedSubjects, subject];
    }

    setSelectedSubjects(nextSelected);

    if (autoApply) {
      applySubjectFilter(nextSelected);
    }
  };

  const handleProjectSelect = (project) => {
    const isSelected = selectedProjects.some(p => p.id === project.id);
    let newSelectedProjects;

    if (isSelected) {
      newSelectedProjects = selectedProjects.filter(p => p.id !== project.id);
    } else {
      if (selectedProjects.length >= 4) {
        toast.warning("Maximum 4 projects can be selected");
        return;
      }
      newSelectedProjects = [...selectedProjects, project];
    }
    
    setSelectedProjects(newSelectedProjects);
    
    try {
      if (newSelectedProjects.length > 0) {
        sessionStorage.setItem('contentBankDashboard_selectedProjectIds', JSON.stringify(newSelectedProjects.map(p => p.id)));
      } else {
        sessionStorage.removeItem('contentBankDashboard_selectedProjectIds');
        if (activeFilter === 'project') {
          sessionStorage.removeItem('contentBankDashboard_activeFilter');
        }
      }
    } catch (e) { /* ignore */ }

    if (autoApply) {
      applyProjectFilter(newSelectedProjects);
    }
  };

  const selectedSubjectIds = selectedSubjects.map((s) => s.subject_id);
  const selectedProjectIds = selectedProjects.map((p) => p.id);
  const hasPendingSelection =
    !autoApply &&
    ((selectedSubjects.length > 0 && activeFilter !== 'subject') ||
      (selectedProjects.length > 0 && activeFilter !== 'project'));
  const showSubjectResults =
    !loading && !error && activeFilter !== 'project' && Object.keys(subjectContent).length > 0;
  const showProjectResults =
    !loading && !error && activeFilter === 'project' && projectSummary.length > 0;

  const renderContentEmptyState = () => {
    if (loading || error) return null;

    if (hasPendingSelection) {
      return (
        <EmptyState
          icon={PendingActionsOutlinedIcon}
          title="Selections ready to apply"
          description="You have filters selected. Click Apply on the subject or project panel to load content."
          action={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              {selectedSubjects.length > 0 && activeFilter !== 'subject' && (
                <Button variant="primary" size="small" onClick={handleApplyFilter}>
                  Apply subjects
                </Button>
              )}
              {selectedProjects.length > 0 && activeFilter !== 'project' && (
                <Button variant="primary" size="small" onClick={() => handleApplyProjectFilter()}>
                  Apply projects
                </Button>
              )}
            </Box>
          }
        />
      );
    }

    if (activeFilter === 'project' && projectSummary.length === 0) {
      return (
        <EmptyState
          icon={SearchOffOutlinedIcon}
          title="No project summary"
          description={
            selectedProjects.length > 0
              ? 'No content summary was returned for the selected projects.'
              : 'Select up to 4 projects above to view assignment and approval progress.'
          }
        />
      );
    }

    if (activeFilter === 'subject' && Object.keys(subjectContent).length === 0) {
      return (
        <EmptyState
          icon={SearchOffOutlinedIcon}
          title="No subject content found"
          description="The selected subjects do not have topic data yet, or content could not be loaded."
        />
      );
    }

    if (!activeFilter && selectedSubjects.length === 0 && selectedProjects.length === 0) {
      return (
        <EmptyState
          icon={FilterListOutlinedIcon}
          title="Choose what to work on"
          description="Select up to 4 subjects or projects from the filters above. Turn on auto-apply to load results as you select."
        />
      );
    }

    return null;
  };

  return (
    <Box sx={layoutStyles.container}>
        {/* Filter Section - Always show filters */}
        <Paper
          variant="outlined"
          sx={{
            flexShrink: 0,
            p: 2,
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'background.paper',
          }}
        >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                Filters
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={autoApply}
                    onChange={(e) => setAutoApply(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Auto-apply on selection
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
              <FilterTileGroup
                title="Subject Filter"
                items={subjects}
                selectedIds={selectedSubjectIds}
                getItemId={(s) => s.subject_id}
                getItemLabel={(s) => s.subject_name}
                onToggle={handleSubjectSelect}
                onApply={handleApplyFilter}
                isActive={activeFilter === 'subject'}
                loading={loading}
                showApplyButton={!autoApply}
                emptyMessage="No subjects available"
              />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
              <FilterTileGroup
                title="Project Filter"
                items={projects}
                selectedIds={selectedProjectIds}
                getItemId={(p) => p.id}
                getItemLabel={(p) => p.project_name}
                onToggle={handleProjectSelect}
                onApply={() => handleApplyProjectFilter()}
                isActive={activeFilter === 'project'}
                loading={loading}
                showApplyButton={!autoApply}
                emptyMessage="No projects available"
              />
              </Box>
            </Box>
        </Paper>

        {/* Content Area */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'background.paper',
          }}
        >
          {loading && <LoadingState message="Loading content…" layout="centered" sx={{ py: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {!loading &&
            !error &&
            activeFilter === "project" &&
            projectSummary.length > 0 && (
              <div
                className="table-responsive"
                style={layoutStyles.projectsContainer}
              >
                <table
                  className="table table-sm mb-0 align-middle"
                  style={{ 
                    fontSize: "0.75rem",
                    tableLayout: "fixed",
                    width: "100%",
                    borderCollapse: "separate", 
                    borderSpacing: 0,
                  }}
                >
                  <thead style={{ ...layoutStyles.stickyHeader, position: "sticky", top: 0, backgroundColor: "var(--color-bg-subtle)" }}>
                    <tr>
                      <th style={projectTableColumnStyles.sno}>#</th>
                      <th style={{ ...projectTableColumnStyles.projectName, textAlign: "center" }}>Project name</th>
                      <th style={projectTableColumnStyles.company}>Company</th>
                      <th style={projectTableColumnStyles.state}>State</th>
                      <th style={projectTableColumnStyles.modified}>Modified</th>
                      <th style={projectTableColumnStyles.domain}>Domain</th>
                      <th style={projectTableColumnStyles.description}>Description</th>
                      <th style={projectTableColumnStyles.contentType}>Content Type</th>
                      {!shouldHideCountColumns && (
                        <>
                          <th style={projectTableColumnStyles.count}>Total</th>
                          <th style={projectTableColumnStyles.count}>Selected</th>
                        </>
                      )}
                      <th style={projectTableColumnStyles.count}>Assigned</th>
                      <th style={projectTableColumnStyles.count}>Uploaded</th>
                      <th style={projectTableColumnStyles.count}>Approved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectSummary.map((project, projectIndex) => {
                      const isExpanded = expandedProjects.has(
                        project.project_id
                      );
                        const contentTypes = [
                          { key: ContentTypeConstants.VIDEO.key, label: ContentTypeConstants.VIDEO.label, icon: <PiMonitorPlayBold size={14} /> },
                          { key: ContentTypeConstants.NOTES.key, label: ContentTypeConstants.NOTES.label, icon: <SlNotebook size={14} /> },
                          { key: ContentTypeConstants.MCQ.key, label: ContentTypeConstants.MCQ.label, icon: <BsListTask size={14} /> },
                          { key: ContentTypeConstants.CODING.key, label: ContentTypeConstants.CODING.label, icon: <LiaLaptopCodeSolid size={14} /> },
                          {
                            key: ContentTypeConstants.PROJECT_CODING.key,
                            label: ContentTypeConstants.PROJECT_CODING.label,
                            icon: <LiaLaptopCodeSolid size={14} />,
                          },
                        ];

                      return (
                        <React.Fragment key={project.project_id}>
                          {contentTypes.map((contentType, ctIndex) => {
                            const content = project.content?.[contentType.key] ?? {};
                            const isFirstRow = ctIndex === 0;
                            // Alternate row colors: white for even index, light grey for odd index
                            // All rows for the same project should have the same background color
                            const rowBgColor =
                              projectIndex % 2 === 0 ? "#fff" : "#f8f9fa";

                            return (
                              <tr
                                key={`${project.project_id}-${contentType.key}`}
                                style={{ backgroundColor: rowBgColor }}
                              >
                                {isFirstRow && (
                                  <>
                                    <td
                                      rowSpan={5}
                                      style={{
                                        ...projectTableCell,
                                        textAlign: "center",
                                        fontWeight: 500,
                                        backgroundColor: rowBgColor,
                                      }}
                                    >
                                      {projectIndex + 1}
                                    </td>
                                    <td
                                      rowSpan={5}
                                      style={{
                                        ...projectTableColumnStyles.projectName,
                                        ...projectTableCell,
                                        fontWeight: 500,
                                        backgroundColor: rowBgColor,
                                        textAlign: "left",
                                      }}
                                      title={project.project_name}
                                    >
                                        <span
                                          style={{
                                            cursor: "pointer",
                                            color: "#0d6efd",
                                            wordBreak: "break-word",
                                          }}
                                          onClick={() => {
                                            try {
                                              sessionStorage.setItem('projectId', JSON.stringify(project.project_id));
                                              navigate('/project-content', { state: { projectId: project.project_id } });
                                            } catch (e) { /* ignore */ }
                                          }}
                                        >
                                          {project.project_name}
                                        </span>
                                    </td>
                                    <td
                                      rowSpan={5}
                                      style={{
                                        ...projectTableColumnStyles.company,
                                        ...projectTableCell,
                                        backgroundColor: rowBgColor,
                                      }}
                                      title={project.company_name || "-"}
                                    >
                                      {project.company_name || "-"}
                                    </td>
                                    <td
                                      rowSpan={5}
                                      style={{
                                        ...projectTableColumnStyles.state,
                                        ...projectTableCell,
                                        backgroundColor: rowBgColor,
                                      }}
                                    >
                                      {project.project_state || "-"}
                                    </td>
                                    <td
                                      rowSpan={5}
                                      style={{
                                        ...projectTableColumnStyles.modified,
                                        ...projectTableCell,
                                        backgroundColor: rowBgColor,
                                      }}
                                    >
                                      {project.modified_at ? (() => {
                                        // Convert UTC to IST (UTC+5:30)
                                        const utcDate = new Date(project.modified_at);
                                        const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
                                        
                                        // Format date as DD/MM/YYYY (use local methods after adding offset)
                                        const day = String(istDate.getDate()).padStart(2, '0');
                                        const month = String(istDate.getMonth() + 1).padStart(2, '0');
                                        const year = istDate.getFullYear();
                                        
                                        // Format time as 12-hour format without seconds (use local methods after adding offset)
                                        let hours = istDate.getHours();
                                        const minutes = String(istDate.getMinutes()).padStart(2, '0');
                                        const ampm = hours >= 12 ? 'PM' : 'AM';
                                        hours = hours % 12;
                                        hours = hours ? hours : 12; // the hour '0' should be '12'
                                        const formattedHours = String(hours).padStart(2, '0');
                                        
                                        return (
                                          <span style={{ color: "#495057", fontWeight: 500 }}>
                                            {day}/{month}/{String(year).slice(-2)} {formattedHours}:{minutes} {ampm}
                                          </span>
                                        );
                                      })() : "-"}
                                    </td>
                                    <td
                                      rowSpan={5}
                                      style={{
                                        ...projectTableColumnStyles.domain,
                                        ...projectTableCell,
                                        backgroundColor: rowBgColor,
                                      }}
                                      title={project.industry || "-"}
                                    >
                                      {project.industry || "-"}
                                    </td>
                                    <td
                                      rowSpan={5}
                                      style={{
                                        ...projectTableColumnStyles.description,
                                        ...projectTableCell,
                                        backgroundColor: rowBgColor,
                                      }}
                                      title={project.project_description || "-"}
                                    >
                                      {project.project_description || "-"}
                                    </td>
                                  </>
                                )}
                                <td
                                  style={{
                                    ...projectTableColumnStyles.contentType,
                                    ...projectTableCell,
                                    fontWeight: 500,
                                    backgroundColor: rowBgColor,
                                  }}
                                >
                                  <span className="me-1" style={{ display: "inline-flex", verticalAlign: "middle" }}>
                                    {contentType.icon}
                                  </span>
                                  <span style={{ fontSize: "0.72rem" }}>{contentType.label}</span>
                                </td>
                                {!shouldHideCountColumns && (
                                  <>
                                    <td
                                      style={{
                                        ...projectTableColumnStyles.count,
                                        ...projectTableCell,
                                        color: "#0d6efd",
                                        cursor: "pointer",
                                        backgroundColor: rowBgColor,
                                      }}
                                    >
                                      {content.total}
                                    </td>
                                    <td
                                      style={{
                                        ...projectTableColumnStyles.count,
                                        ...projectTableCell,
                                        color: "#0d6efd",
                                        cursor: "pointer",
                                        backgroundColor: rowBgColor,
                                      }}
                                    >
                                      {content.selected}
                                    </td>
                                  </>
                                )}
                                <td
                                  style={{
                                    ...projectTableColumnStyles.count,
                                    ...projectTableCell,
                                    color: "#0d6efd",
                                    cursor: "pointer",
                                    backgroundColor: rowBgColor,
                                  }}
                                >
                                  {content.assigned ?? 0}
                                </td>
                                <td
                                  style={{
                                    ...projectTableColumnStyles.count,
                                    ...projectTableCell,
                                    color: "#0d6efd",
                                    cursor: "pointer",
                                    backgroundColor: rowBgColor,
                                  }}
                                >
                                  {content.uploaded ?? 0}
                                </td>
                                <td
                                  style={{
                                    ...projectTableColumnStyles.count,
                                    ...projectTableCell,
                                    color: "#0d6efd",
                                    cursor: "pointer",
                                    backgroundColor: rowBgColor,
                                  }}
                                >
                                  {content.approved ?? 0}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

          {showSubjectResults && (
              <Box sx={{ p: 1.5, ...layoutStyles.subjectsContainer }}>
                {Object.entries(subjectContent).map(
                  ([subjectName, subjectData]) => {
                    const isExpanded = expandedSubjects.has(subjectName);
                    const totals = calculateTotalCounts(subjectData);
                    const subject = subjects.find(
                      (s) => s.subject_name === subjectName
                    );

                    return (
                      <Paper
                        key={subjectName}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          borderRadius: 'var(--radius-lg)',
                        }}
                      >
                        <Box
                          onClick={() => toggleSubjectExpansion(subjectName)}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1.5,
                            cursor: 'pointer',
                            bgcolor: 'var(--color-bg-subtle)',
                            '&:hover': { bgcolor: 'var(--color-primary-50)' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              component="span"
                              sx={{
                                display: 'inline-block',
                                transition: 'transform 0.3s ease',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                              }}
                            >
                              ▼
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                              {subjectName}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 'var(--radius-md)',
                              border: '1px solid var(--color-border-default)',
                              bgcolor: 'background.paper',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PiMonitorPlayBold size={18} />
                              <Typography variant="body2" fontWeight={600} color="primary.main">{totals.totalVideos}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SlNotebook size={18} />
                              <Typography variant="body2" fontWeight={600} color="primary.main">{totals.totalNotes}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <BsListTask size={18} />
                              <Typography variant="body2" fontWeight={600} color="primary.main">{totals.totalMcq}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LiaLaptopCodeSolid size={18} />
                              <Typography variant="body2" fontWeight={600} color="primary.main">{totals.totalCoding}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AiOutlineFundProjectionScreen size={18} />
                              <Typography variant="body2" fontWeight={600} color="primary.main">{totals.totalProjectCoding}</Typography>
                            </Box>
                          </Box>
                        </Box>

                        {isExpanded && (
                          <Box sx={{ borderTop: '1px solid var(--color-border-default)', ...layoutStyles.subjectContent }}>
                            {subjectData.length === 0 ? (
                              <EmptyState
                                compact
                                title="No topics yet"
                                description="This subject does not have topics configured."
                              />
                            ) : (
                              <TableContainer sx={{ flex: 1, ...layoutStyles.tableScroll }}>
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.sno }}>S.no</TableCell>
                                      <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.topic }}>Topic</TableCell>
                                      <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.subtopic }}>Sub Topic</TableCell>
                                      {!shouldHideCountColumns && (
                                        <>
                                          <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.count }} align="center">Videos</TableCell>
                                          <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.count }} align="center">Notes</TableCell>
                                          <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.count }} align="center">MCQs</TableCell>
                                          <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.count }} align="center">Coding</TableCell>
                                          <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.count }} align="center">Project Coding</TableCell>
                                        </>
                                      )}
                                      <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.action }} align="center">Action</TableCell>
                                      <TableCell sx={{ ...layoutStyles.tableHeadCell, ...columnStyles.create }} align="center">Create</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {subjectData.map((topic, topicIndex) => (
                                      <React.Fragment key={topic.topic_id}>
                                        {topic.sub_topics.map((subTopic, subTopicIndex) => (
                                          <TableRow
                                            key={subTopic.sub_topic_id}
                                            hover
                                            sx={{ '&:last-child td': { borderBottom: 0 } }}
                                          >
                                            <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.sno }}>
                                              {subTopicIndex === 0 ? topicIndex + 1 : ''}
                                            </TableCell>
                                            <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.topic }}>
                                                {subTopicIndex === 0 ? (
                                                <Typography
                                                  component="span"
                                                  variant="body2"
                                                  color="primary"
                                                  onClick={() => handleTopicClick(subject.subject_id, topic.topic_id)}
                                                    title="Click to view topic content"
                                                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                  >
                                                    {topic.topic_name}
                                                </Typography>
                                              ) : ''}
                                            </TableCell>
                                            <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.subtopic }}>
                                              <Typography
                                                component="span"
                                                variant="body2"
                                                color="primary"
                                                onClick={() => handleSubTopicClick(subject.subject_id, topic.topic_id, subTopic.sub_topic_id)}
                                                  title="Click to view sub-topic content"
                                                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                  {subTopic.sub_topic_name}
                                              </Typography>
                                            </TableCell>
                                              {!shouldHideCountColumns && (
                                                <>
                                                <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.count }} align="center">
                                                    {subTopic.video ?? subTopic[ContentTypeConstants.VIDEO.key] ?? 0}
                                                </TableCell>
                                                <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.count }} align="center">
                                                    {subTopic.notes ?? subTopic[ContentTypeConstants.NOTES.key] ?? 0}
                                                </TableCell>
                                                <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.count }} align="center">
                                                    {subTopic.mcq ?? subTopic[ContentTypeConstants.MCQ.key] ?? 0}
                                                </TableCell>
                                                <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.count }} align="center">
                                                    {subTopic.coding ?? subTopic[ContentTypeConstants.CODING.key] ?? 0}
                                                </TableCell>
                                                <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.count }} align="center">
                                                    {subTopic.project_coding ?? subTopic[ContentTypeConstants.PROJECT_CODING.key] ?? 0}
                                                </TableCell>
                                                </>
                                              )}
                                            <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.action }} align="center">
                                              <Button
                                                variant="primary"
                                                size="small"
                                                onClick={() => handleViewButtonClick(subject.subject_id, topic.topic_id, subTopic.sub_topic_id)}
                                                >
                                                  View
                                              </Button>
                                            </TableCell>
                                            <TableCell sx={{ ...layoutStyles.tableBodyCell, ...columnStyles.create }} align="center">
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleAddContentClick(subject.subject_id, topic.topic_id, subTopic.sub_topic_id)}
                                                sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: '50%' }}
                                                aria-label="Add content"
                                              >
                                                +
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  }
                )}
              </Box>
            )}

          {!showSubjectResults && !showProjectResults && renderContentEmptyState()}
        </Paper>
    </Box>
  );
}

export default ContentBankDashboard;

